import dayjs from 'dayjs';
import {
	ResponsiveContainer,
	AreaChart,
	CartesianGrid,
	Area,
	XAxis,
	YAxis,
	TooltipProps,
	Tooltip as ChartsTooltip,
} from 'recharts';
import { getAreaStops } from 'src/utils/graph';
import {
	BigNum,
	PERCENTAGE_PRECISION,
	PRICE_PRECISION_EXP,
	PublicKey,
	QUOTE_PRECISION_EXP,
	SpotMarketConfig,
} from '@drift-labs/sdk';
import { UIMarket } from '@drift/common';
import {
	GraphType,
	Period,
	getYDomain,
	VaultHistoryGraphTooltip,
} from '../VaultGraphs';
import { LoadingChart } from 'src/components/Charts/LoadingChart';
import {
	useVaultSnapshotHistory,
	VaultSnapshotEssentials,
} from 'src/hooks/query/vaults/base/useVaultSnapshotHistory';
import { useVaultsStore } from 'src/stores/vaultsStore/useVaultsStore';
import { useVaultStats } from 'src/hooks/query/vaults/derived/useVaultStats';
import { useEffect, useState } from 'react';
import { UiVaults } from 'src/constants/vaults';
import { VAULT_SHARES_PRECISION_EXP } from '@drift-labs/vaults-sdk';
import handleSpecialRendering from 'src/utils/handleSpecialRendering';
import { calculateApy } from '../../../../app/api/cron/vaults/update-apy-returns-v2/route';
import { useSpotMarketsLookup } from 'src/hooks/spotMarkets/useSpotMarketsLookup';
import { getColorVar } from 'src/utils/theme/colorVars';
import useOraclePriceStore from 'src/stores/useOraclePriceStore';

const CUSTOM_LINE_COLORS_ID = 'custom-line-colors_vault-graph';
const CUSTOM_AREA_COLORS_ID = 'custom-area-colors_vault-graph';

export const VaultHistoryGraph = (props: {
	vaultPubkey: string;
	depositAssetMarketIndex: number;
	graphType: GraphType;
	period: Period;
}) => {
	const uiMarket = UIMarket.createSpotMarket(props.depositAssetMarketIndex);
	const uiVaultConfig = UiVaults.getVaultConfig(props.vaultPubkey);
	const precisionExp = (uiMarket.market as SpotMarketConfig).precisionExp;

	const dataKey =
		props.graphType === 'pnl'
			? 'pnl'
			: props.graphType === 'balance'
				? 'baseBalance'
				: props.graphType === 'roi'
					? 'roi'
					: props.graphType === 'apy'
						? 'apy'
						: 'sharePrice';

	const isUsdcAsset =
		props.depositAssetMarketIndex === 0 ||
		(props.graphType === 'pnl' && uiVaultConfig?.isNotionalGrowthStrategy);

	const { data: vaultSnapshots, isLoading: isVaultSnapshotsLoading } =
		useVaultSnapshotHistory(props.vaultPubkey);
	const { data: vaultStat } = useVaultStats(props.vaultPubkey);
	const vaultClient = useVaultsStore((s) => s.vaultClient);
	const spotMarketsLookup = useSpotMarketsLookup();

	const performanceFeeFraction =
		(vaultStat?.profitShare ?? 0) / PERCENTAGE_PRECISION.toNumber();
	const managementFeeFraction =
		(vaultStat?.vaultAccount?.managementFee?.toNumber() ?? 0) /
		PERCENTAGE_PRECISION.toNumber();

	const [displayedData, setDisplayedData] = useState<
		{
			ts: number;
			pnl: number;
			baseBalance: number;
			sharePrice: number;
			roi: number;
			apy: number;
		}[]
	>([]);

	useEffect(() => {
		if (!vaultSnapshots?.length) return;
		getAndSetDisplayedData().then((data) => {
			setDisplayedData(data);
		});
	}, [vaultSnapshots?.length, precisionExp, props.period]);

	const isLoading = isVaultSnapshotsLoading || !displayedData.length;

	const minX = displayedData.reduce(
		(acc, curr) => Math.min(acc, curr.ts),
		Infinity
	);
	const maxX = displayedData.reduce(
		(acc, curr) => Math.max(acc, curr.ts),
		-Infinity
	);
	const xDomain = [minX, maxX];

	const minY = displayedData.reduce(
		(acc, curr) => Math.min(acc, curr[dataKey]),
		Infinity
	);
	const maxY = displayedData.reduce(
		(acc, curr) => Math.max(acc, curr[dataKey]),
		-Infinity
	);
	const yDomain = getYDomain(minY, maxY);

	const getSharePriceForSnapshot = ({
		totalShares,
		totalAccountBaseValue,
		oraclePrice,
	}: Pick<
		VaultSnapshotEssentials,
		'totalShares' | 'totalAccountBaseValue' | 'oraclePrice'
	>) => {
		let sharePrice = BigNum.from(totalAccountBaseValue, precisionExp)
			.shift(VAULT_SHARES_PRECISION_EXP)
			.div(BigNum.from(totalShares, VAULT_SHARES_PRECISION_EXP))
			.toNum();

		if (uiVaultConfig?.isNotionalGrowthStrategy) {
			sharePrice =
				sharePrice * BigNum.from(oraclePrice, PRICE_PRECISION_EXP).toNum();
		}

		return sharePrice;
	};

	async function getAndSetDisplayedData() {
		const cutoffDays =
			props.period === '7d'
				? 7
				: props.period === '30d'
					? 30
					: props.period === '90d'
						? 90
						: 0;
		const cutoffDate = dayjs().subtract(cutoffDays, 'day').subtract(1, 'day'); // subtract 1 day to include starting point of APY calculation (e.g. 7d period should include from previous Sunday to current Sunday)

		const periodData = vaultSnapshots.filter((snapshot) => {
			return dayjs.unix(+snapshot.ts).isAfter(cutoffDate);
		});

		// not sure if this is correct but avoid possible page bombing
		if (!periodData.length) {
			return [];
		}

		const getRoiForSharePrice = (
			initialSharePrice: number,
			currentSharePrice: number
		) => {
			const changeFactor =
				(currentSharePrice - initialSharePrice) / initialSharePrice;
			return changeFactor * 100;
		};

		const firstSnapshotSharePrice = getSharePriceForSnapshot(periodData[0]);

		const periodSnapshots: {
			ts: number;
			pnl: number;
			baseBalance: number;
			sharePrice: number;
			roi: number;
			apy: number;
		}[] = periodData.map((snapshot, index) => {
			const basePnl = BigNum.from(
				snapshot.totalAccountBaseValue,
				precisionExp
			).sub(BigNum.from(snapshot.netDeposits, precisionExp));

			const netQuoteDeposits = BigNum.from(
				snapshot.netQuoteDeposits ?? 0,
				QUOTE_PRECISION_EXP
			);
			const quoteTvl = BigNum.from(
				snapshot.totalAccountQuoteValue,
				QUOTE_PRECISION_EXP
			);
			const notionalPnl = quoteTvl.sub(netQuoteDeposits);

			const pnl = uiVaultConfig?.isNotionalGrowthStrategy
				? notionalPnl
				: basePnl;

			if (snapshot.totalShares === '0') {
				return {
					ts: +snapshot.ts,
					pnl: pnl.toNum(),
					baseBalance: BigNum.from(
						snapshot.totalAccountBaseValue,
						precisionExp
					).toNum(),
					sharePrice: 0,
					roi: 0,
					apy: 0,
				};
			}

			const sharePrice = getSharePriceForSnapshot(snapshot);

			return {
				ts: +snapshot.ts,
				pnl: pnl.toNum(),
				baseBalance: BigNum.from(
					snapshot.totalAccountBaseValue,
					precisionExp
				).toNum(),
				sharePrice,
				roi: getRoiForSharePrice(firstSnapshotSharePrice, sharePrice),
				apy:
					calculateApy(
						firstSnapshotSharePrice,
						sharePrice,
						index,
						performanceFeeFraction,
						managementFeeFraction
					) * 100,
			};
		});

		// add current live data point to graph
		if (vaultStat) {
			const vaultUser = await vaultClient?.getSubscribedVaultUser(
				uiVaultConfig?.driftUserPubkeyString
					? new PublicKey(uiVaultConfig.driftUserPubkeyString)
					: PublicKey.default
			);
			const vaultNetQuoteDeposits = vaultUser
				?.getUserAccount()
				.totalDeposits.sub(vaultUser.getUserAccount().totalWithdraws);
			const currentNotionalPnl = vaultStat.tvlQuote.sub(
				BigNum.from(vaultNetQuoteDeposits, QUOTE_PRECISION_EXP)
			);

			const pnl = uiVaultConfig?.isNotionalGrowthStrategy
				? currentNotionalPnl
				: vaultStat.totalBasePnl;

			let currentDataPoint;

			if (vaultStat.totalShares.eqZero()) {
				currentDataPoint = {
					ts: dayjs().unix(),
					pnl: pnl.toNum(),
					baseBalance: vaultStat.tvlBase.toNum(),
					sharePrice: 0,
					roi: 0,
					apy: 0,
				};
			} else {
				let sharePrice = vaultStat.tvlBase
					.shift(VAULT_SHARES_PRECISION_EXP)
					.div(vaultStat.totalShares)
					.toNum();

				if (uiVaultConfig?.isNotionalGrowthStrategy) {
					// Get oracle price directly from store to avoid subscription-based re-renders
					const priceData = useOraclePriceStore
						.getState()
						.getMarketPriceData(uiMarket.marketId);
					const currentOraclePrice = priceData?.rawPriceData.price
						? BigNum.from(priceData.rawPriceData.price, PRICE_PRECISION_EXP)
						: null;
					sharePrice = sharePrice * (currentOraclePrice?.toNum() || 1);
				}

				currentDataPoint = {
					ts: dayjs().unix(),
					pnl: pnl.toNum(),
					baseBalance: vaultStat.tvlBase.toNum(),
					sharePrice,
					roi: getRoiForSharePrice(firstSnapshotSharePrice, sharePrice),
					apy:
						calculateApy(
							firstSnapshotSharePrice,
							sharePrice,
							cutoffDays,
							performanceFeeFraction,
							managementFeeFraction
						) * 100,
				};
			}

			if (
				dayjs.unix(periodSnapshots.slice(-1)[0]?.ts).date() !== dayjs().date()
			) {
				periodSnapshots.push(currentDataPoint);
			} else {
				// replace the last data point with the current data point if they are on the same day
				periodSnapshots[periodSnapshots.length - 1] = currentDataPoint;
			}
		}

		return periodSnapshots;
	}

	if (isLoading) return <LoadingChart className="mt-6 h-[258px]" />;

	return (
		<div className="mt-4 flex h-full min-h-[400px] w-full flex-col gap-2 text-text-default">
			<ResponsiveContainer width={'100%'} className="h-[200px] grow">
				<AreaChart data={displayedData}>
					<CartesianGrid
						stroke={getColorVar('stroke-divider')}
						vertical={false}
					/>

					<defs>
						<linearGradient
							id={CUSTOM_LINE_COLORS_ID}
							x1="0"
							y1="0"
							x2="0"
							y2="1"
						>
							{getAreaStops(minY, maxY)}
						</linearGradient>
					</defs>
					<defs>
						<linearGradient
							id={CUSTOM_AREA_COLORS_ID}
							x1="0"
							y1="0"
							x2="0"
							y2="1"
						>
							{getAreaStops(minY, maxY, {
								startOpacity: 0.6,
								endOpacity: 0.2,
							})}
						</linearGradient>
					</defs>
					<Area
						type="monotone"
						fill={`url(#${CUSTOM_AREA_COLORS_ID})`}
						stroke={`url(#${CUSTOM_LINE_COLORS_ID})`}
						strokeWidth={2}
						dataKey={dataKey}
					/>

					<XAxis
						tickCount={40}
						tickMargin={8}
						dataKey="ts"
						domain={xDomain}
						tickLine={false}
						tickFormatter={(tick) => {
							return dayjs.unix(tick).format('DD/MM');
						}}
						stroke={getColorVar('stroke-divider')}
					/>
					<YAxis
						tickMargin={8}
						tickLine={false}
						stroke={getColorVar('stroke-divider')}
						dataKey={dataKey}
						// @ts-ignore
						domain={yDomain}
						tickFormatter={(tick: number) => {
							if (props.graphType === 'roi' || props.graphType === 'apy') {
								return `${tick.toFixed(2)}%`;
							}
							if (props.graphType === 'sharePrice') {
								return tick.toPrecision(4);
							}
							return `${tick < 0 ? '-' : ''}${
								isUsdcAsset ? '$' : ''
							}${handleSpecialRendering(tick.toString())}`;
						}}
					/>

					<ChartsTooltip
						content={(tooltipProps: TooltipProps<number, string>) => (
							<VaultHistoryGraphTooltip
								{...tooltipProps}
								marketConfig={
									(props.graphType === 'sharePrice' ||
										props.graphType === 'pnl') &&
									uiVaultConfig?.isNotionalGrowthStrategy
										? spotMarketsLookup[0]
										: (uiMarket.market as SpotMarketConfig)
								}
								isPnl={props.graphType === 'pnl'}
								isPct={props.graphType === 'roi' || props.graphType === 'apy'}
							/>
						)}
						cursor={{
							stroke: getColorVar('text-secondary'),
							strokeDasharray: '4',
						}}
					/>
				</AreaChart>
			</ResponsiveContainer>
		</div>
	);
};
